import { useMemo, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { Organization } from "../Models/Organization";

interface IProps {
  organizations: Organization[];
  selectedOrganizationIds: number[];
  onChange: (organizationIds: number[]) => void;
  disabled?: boolean;
}

export default function OrganizationFilter(props: IProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const selectedOrganizations = useMemo(
    () => props.organizations.filter(_ => props.selectedOrganizationIds.includes(_.id)),
    [props.organizations, props.selectedOrganizationIds]
  );
  const filteredOrganizations = useMemo(
    () => normalizedSearchTerm
      ? props.organizations.filter(_ => _.name.toLocaleLowerCase().includes(normalizedSearchTerm))
      : props.organizations,
    [props.organizations, normalizedSearchTerm]
  );

  const toggleOrganization = (organizationId: number, checked: boolean) => {
    const nextIds = checked
      ? [...props.selectedOrganizationIds, organizationId]
      : props.selectedOrganizationIds.filter(_ => _ !== organizationId);

    props.onChange(Array.from(new Set(nextIds)));
  };

  const selectionSummary = selectedOrganizations.length === 0
    ? "All organizations"
    : selectedOrganizations.length === 1
      ? selectedOrganizations[0].name
      : `${selectedOrganizations.length} organizations selected`;

  return (
    <Dropdown
      className="aa-organization-filter"
      autoClose="outside"
      onToggle={isOpen => { if (!isOpen) setSearchTerm(""); }}
    >
      <Dropdown.Toggle
        id="OrganizationFilter"
        className="aa-organization-filter__toggle"
        variant="outline-secondary"
        disabled={props.disabled}
      >
        <span className="text-truncate">{selectionSummary}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="aa-organization-filter__menu">
        <div className="px-3 pt-2 pb-1">
          <Form.Control
            type="search"
            size="sm"
            autoFocus
            aria-label="Search organizations"
            placeholder="Type to search"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="aa-organization-filter__options" role="group" aria-label="Organizations">
          {filteredOrganizations.map(organization =>
            <Form.Check
              key={organization.id}
              id={`OrganizationIds-${organization.id}`}
              type="checkbox"
              label={organization.name}
              checked={props.selectedOrganizationIds.includes(organization.id)}
              onChange={event => toggleOrganization(organization.id, event.target.checked)}
            />
          )}
          {filteredOrganizations.length === 0 &&
            <p className="text-muted mb-0 px-1 py-2">No organizations match your search.</p>
          }
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
